/* eslint-disable perfectionist/sort-classes */
/* eslint-disable max-lines */
import {
  CellClickedEvent,
  ColDef,
  ExcelExportParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ITooltipParams,
} from '@ag-grid-community/core';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgSelectComponent } from '@ng-select/ng-select';
import { BehaviorSubject, combineLatest, firstValueFrom, interval, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { ToggleCurrencyComponent } from '@widgets/toggle-currency/toggle-currency.component';

import { ExportExcelButtonComponent } from '@features/export-excel-button/export-excel-button.component';
import { SiteSelectDropdownComponent } from '@features/site-select-dropdown/site-select-dropdown.component';

import { PatientGroupsService } from '@models/patient-group/patient-groups.service';
import { PatientProtocolQuery } from '@models/patient-protocol/patient-protocol.query';
import { PatientProtocolService } from '@models/patient-protocol/patient-protocol.service';
import { SitesQuery } from '@models/sites/sites.query';
import { SitesService } from '@models/sites/sites.service';
import { SitesStore } from '@models/sites/sites.store';

import { AgCellWrapperComponent } from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import { CardComponent } from '@shared/components/card/card.component';
import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { PaginationGridComponent } from '@shared/components/pagination-grid/pagination-grid.component';
import { ToggleComponent } from '@shared/components/toggle/toggle.component';
import { LocalStorageKey } from '@shared/constants/localStorageKey';
import { initialPaginationSize } from '@shared/constants/pagination.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { MoneyPipe } from '@shared/pipes/money.pipe';
import { DatasourceService, PatientVisitDataSteam } from '@shared/services/datasource.service';
import {
  Currency,
  EntityType,
  EventType,
  PatientInvoiceable,
  PatientProtocolType,
  PatientVisit,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { FormValuesQuery } from '@shared/store/form-values/form-values.query';
import { MainQuery } from '@shared/store/main/main.query';
import { CurrencyToggle, Option } from '@shared/types/components.type';
import {
  AgSetColumnsVisible,
  decimalAdd,
  ServerSideColumnFilterType,
  ServerSideFilterInfo,
  ServerSideSortOrder,
} from '@shared/utils';
import { ExportType, Utils } from '@shared/utils/utils';

import { PatientTrackerScheduleService } from './services';
import { PatientTrackerRow } from './types';
import { getPatientCostColumns, getPatientsColumn, getPatientVisitColumns } from './utils';

interface FilterForm {
  has_patient_group: boolean;
  patientGroupId: null | string;
  siteIds: string[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardComponent,
    NgClass,
    IconComponent,
    CheckboxComponent,
    FormsModule,
    ReactiveFormsModule,
    TooltipDirective,
    ExportExcelButtonComponent,
    NoopValueAccessorDirective,
    SiteSelectDropdownComponent,
    NgSelectComponent,
    ToggleComponent,
    ToggleCurrencyComponent,
    PaginationGridComponent,
    RouterLink,
    MoneyPipe,
    AsyncPipe,
    DatePipe,
  ],
  selector: 'aux-patient-tracker',
  styleUrls: ['patient-tracker.component.scss'],
  templateUrl: './patient-tracker.component.html',
})
export class PatientTrackerComponent {
  public sitesQuery = inject(SitesQuery);

  private datasourceService = inject(DatasourceService);
  private readonly destroyRef = inject(DestroyRef);
  private eventService = inject(EventService);
  private formBuilder = inject(FormBuilder);
  private formValuesQuery = inject(FormValuesQuery);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private patientGroupService = inject(PatientGroupsService);
  private patientProtocolQuery = inject(PatientProtocolQuery);
  private patientProtocolService = inject(PatientProtocolService);
  private patientTrackerScheduleService = inject(PatientTrackerScheduleService);
  private router = inject(Router);
  private sitesService = inject(SitesService);
  private sitesStore = inject(SitesStore);

  plannedThroughCurrentMonth = signal(0);
  plannedThroughEOT = signal(0);
  showGrid = signal(false);
  totalPatientVisitsLTD = signal(0);

  private visitCostsOptionValue = 'visitCosts';

  serverSideFilters: ServerSideFilterInfo<PatientVisitDataSteam>[] = [
    {
      column: 'site_id',
      inputPropertyName: 'siteIds',
      type: ServerSideColumnFilterType.IsEqualTo,
    },
    {
      column: 'patient_group_id',
      inputPropertyName: 'patientGroupId',
      transformFunction: (v: unknown) => (v !== this.visitCostsOptionValue ? v : null),
      type: ServerSideColumnFilterType.IsEqualTo,
    },
    {
      column: 'has_patient_group',
      inputPropertyName: 'has_patient_group',
      transformFunction: () => {
        const gridFilterValues = this.gridFiltersFormGroup.getRawValue();

        return (
          !!gridFilterValues.patientGroupId &&
          gridFilterValues.patientGroupId !== null &&
          gridFilterValues.patientGroupId !== this.visitCostsOptionValue
        );
      },
      type: ServerSideColumnFilterType.IsEqualTo,
    },
  ];

  readonly paginationKey = LocalStorageKey.PAGINATION_PATIENT_TRACKER;

  gridFiltersFormGroup = this.formBuilder.group<FilterForm>(
    this.serverSideFilters.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.inputPropertyName]: [],
      }),
      {
        has_patient_group: true,
        patientGroupId: null,
        siteIds: [] as string[],
      }
    )
  );

  currencyToggle = CurrencyToggle;
  currentOpenMonth$ = this.mainQuery.select('currentOpenMonth');
  datasource = this.datasourceService.patientVisitScheduleDatasource;
  display$ = new BehaviorSubject<'costs' | 'dates'>('dates');
  exportGridApi!: GridApi;
  filterValues$ = new BehaviorSubject<Record<string, unknown>>({});
  gridAPI!: GridApi;
  gridDataLoading$ = new BehaviorSubject(true);
  gridOptions: ExcelExportParams = {
    fileName: 'auxilius-patient-tracker.xlsx',
    sheetName: 'Patient Tracker',
  };
  selectedVisibleCurrency$ = new BehaviorSubject<CurrencyToggle>(CurrencyToggle.PRIMARY);
  gridOptions$ = new BehaviorSubject<GridOptions>({
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      cellRenderer: AgCellWrapperComponent,
      minWidth: 120,
    },
    paginationPageSize: initialPaginationSize(LocalStorageKey.PAGINATION_PATIENT_TRACKER),
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    columnTypes: Utils.columnTypes,
    onFilterChanged: (params) => {
      const rows: PatientTrackerRow[] = [];

      params.api?.forEachNodeAfterFilter((r) => r.data && rows.push(r.data));

      this.renderPinnedRow(this.selectedVisibleCurrency$.getValue());
    },
  });
  hasOnlyOneCurrency = false;
  investigatorTransactionRoute = `${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.INVESTIGATOR_TRANSACTIONS}`;
  isContractedCurrency = false;
  isDisplayCosts = false;
  isHandlingUpload$ = new BehaviorSubject(false);
  isLoadingData$ = new BehaviorSubject(true);
  isPlannedVisitsEnabled = this.launchDarklyService.$select(
    (flags) => flags.patient_tracker_planned_visits
  );
  patientGroupOptions$ = new BehaviorSubject<Option[]>([]);
  protocolEntryLink = `/${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.PROTOCOL_ENTRY.INDEX}`;
  protocolVersionControl = new FormControl('');
  protocolVersionOptions$ = new BehaviorSubject<Option[]>([]);
  showForecastCostThroughEOT = new BehaviorSubject(false);
  showForecastCostThroughEOT$: Observable<boolean>;
  showPlannedVisits = signal(false);
  showPlannedVisitsControl = new FormControl(false);
  showPannedEOT = signal(false);
  showPannedEOTControl = new FormControl(false);
  sortModel$ = new BehaviorSubject<ServerSideSortOrder<PatientVisitDataSteam>[]>([]);
  exportLoading = signal(false);

  private renderedNodesCount = 0;

  private clearPinnedBottomRow() {
    this.gridAPI?.setGridOption('pinnedBottomRowData', []);
  }

  private get columnSuffix(): string {
    return this.display$.getValue() === 'costs' ? '::costs' : '::dates';
  }

  // eslint-disable-next-line max-lines-per-function
  constructor() {
    this.launchDarklyService
      .select$((flags) => flags.visit_costs)
      .pipe(takeUntilDestroyed())
      .subscribe((isEnabled) => {
        const optionsWithoutVisitCostOption = this.patientGroupOptions$
          .getValue()
          .filter(({ value }) => value !== this.visitCostsOptionValue);

        if (isEnabled) {
          this.patientGroupOptions$.next([
            ...optionsWithoutVisitCostOption,
            {
              label: 'Visit Costs',
              value: this.visitCostsOptionValue,
            },
          ]);
        } else {
          this.patientGroupOptions$.next([...optionsWithoutVisitCostOption]);
        }
      });

    this.showForecastCostThroughEOT$ = this.launchDarklyService.select$(
      (flags) => flags.section_forecast_cost_through_eot
    );

    this.showForecastCostThroughEOT$.subscribe((flag) => {
      this.showForecastCostThroughEOT.next(flag);
    });

    this.showPlannedVisitsControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((show) => {
      this.showPlannedVisits.set(!!show);
      this.refreshCellsStyles();
    });

    this.showPannedEOTControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((show) => {
      const checked = !!show;

      this.showPannedEOT.set(checked);
      this.showPlannedVisits.set(checked);
      this.showPlannedVisitsControl.setValue(checked);

      this.refreshCellsStyles();
    });

    // Trigger valueFormatter for multiple currencies
    this.selectedVisibleCurrency$
      .pipe(takeUntilDestroyed())
      .subscribe((selectedVisibleCurrency) => {
        this.gridAPI?.refreshCells();
        this.renderPinnedRow(selectedVisibleCurrency);
      });

    this.eventService
      .select$(EventType.BULK_EDC_TEMPLATE_UPLOADED)
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.gridAPI?.refreshServerSide({ purge: true }))
      )
      .subscribe(() => {
        this.isHandlingUpload$.next(false);
      });

    this.fetchVisits();

    this.sitesService.get().pipe(takeUntilDestroyed()).subscribe();

    this.gridFiltersFormGroup
      .get('siteIds')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        this.formValuesQuery.updateValues(
          {
            selectedCurrencies: { isContractCurrency: true, isPrimaryCurrency: true },
            site_ids: (value || []) as string[],
          },
          'patientTracker'
        );
      });

    this.fetchFilterListOptions();
    this.fetchAnalyticCardData();

    this.display$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.setVisibilityVisitColumns();
      this.renderPinnedRow(this.selectedVisibleCurrency$.getValue());
    });

    this.protocolVersionControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      if (this.gridAPI) {
        this.gridAPI?.refreshServerSide({ purge: true });
      }
    });

    this.eventService
      .select$(EventType.UPDATE_INVESTIGATOR_SCHEDULES)
      .pipe(takeUntilDestroyed())
      .subscribe(this.initializeDataStream);

    interval(300)
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        const visibleNodesCount = this.getVisibleRowData().length;

        if (this.renderedNodesCount !== visibleNodesCount) {
          this.renderedNodesCount = visibleNodesCount;
          this.renderPinnedRow(this.selectedVisibleCurrency$.getValue());
        }
      });
  }

  private fetchAnalyticCardData() {
    const patientGroupInput = {
      filter_model: [{ column: 'has_patient_group', op: 'EQ', values: [true] }],
    };

    this.datasource.refresh$
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      .subscribe(async () => {
        try {
          const analyticsData =
            await this.datasourceService.retrievePatientVisitScheduleAnalytics(patientGroupInput);

          patientGroupInput.filter_model[0].values = [false];

          const visitCostAnalyticData =
            await this.datasourceService.retrievePatientVisitScheduleAnalytics(patientGroupInput);

          const totalPatientVisitsLTD = decimalAdd(
            analyticsData.aggregation.patient_visit_ltd_amount ?? 0,
            visitCostAnalyticData.aggregation.patient_visit_ltd_amount ?? 0
          );

          const plannedThroughCurrentMonth = decimalAdd(
            analyticsData.aggregation.current_planned_amount ?? 0,
            visitCostAnalyticData.aggregation.current_planned_amount ?? 0
          );

          const plannedThroughEOT = decimalAdd(
            analyticsData.aggregation.total_amount ?? 0,
            visitCostAnalyticData.aggregation.total_amount ?? 0
          );

          this.totalPatientVisitsLTD.set(totalPatientVisitsLTD);
          this.plannedThroughEOT.set(plannedThroughEOT);
          this.plannedThroughCurrentMonth.set(plannedThroughCurrentMonth);
        } catch (err) {
          console.error('Failed to refresh analytics', err);
        }
      });
  }

  refreshCellsStyles() {
    this.gridAPI?.refreshCells();
    this.renderPinnedRow(this.selectedVisibleCurrency$.getValue());
  }

  private fetchFilterListOptions() {
    combineLatest([
      this.patientProtocolService.getPatientProtocolVersions(),
      this.patientGroupService.getPatientGroupOptions$(),
      this.sitesService.get().pipe(map(({ data }) => data)),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([protocolVersions, patientGroups, sites]) => {
        if (sites?.[0]) {
          this.showGrid.set(sites.length > 0);
          this.sitesStore.setActive([sites[0].id]);
          const cachedSites = this.formValuesQuery.getValuesByFormName('patientTracker').site_ids;

          this.gridFiltersFormGroup.patchValue({
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            siteIds: (cachedSites ? cachedSites : [sites[0].id]) as string[],
          });
        } else {
          this.showGrid.set(false);
          this.gridDataLoading$.next(false);
        }

        this.patientGroupOptions$.next([...patientGroups, ...this.patientGroupOptions$.getValue()]);

        this.protocolVersionOptions$.next(
          protocolVersions.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        );

        if (this.patientGroupOptions$.getValue().length) {
          this.gridFiltersFormGroup.patchValue({
            patientGroupId: this.patientGroupOptions$.getValue()[0].value,
          });
        }

        if (this.protocolVersionOptions$.getValue().length) {
          this.protocolVersionControl.setValue(this.protocolVersionOptions$.getValue()[0].value);
        }

        this.isLoadingData$.next(false);
      });
  }

  private fetchVisits() {
    combineLatest([
      this.gridFiltersFormGroup.get('patientGroupId')!.valueChanges,
      this.protocolVersionControl.valueChanges,
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(([patientGroupId]) => {
          const protocols = this.patientProtocolService.get(
            [
              PatientProtocolType.PATIENT_PROTOCOL_PATIENT_VISIT,
              PatientProtocolType.PATIENT_PROTOCOL_DISCONTINUED,
              PatientProtocolType.PATIENT_PROTOCOL_SCREEN_FAIL,
            ],
            this.getGroupId(patientGroupId),
            false,
            this.protocolVersionControl.value ?? '',
            true
          ) as Observable<GraphqlResponse<Array<PatientInvoiceable | PatientVisit>>>;

          return patientGroupId
            ? protocols.pipe(
                tap(() => {
                  const columns = Object.values(this.patientProtocolQuery.getValue().entities || {})
                    .map((protocol) => {
                      let visitSortIndex: number;

                      switch (protocol.patient_protocol_type) {
                        case PatientProtocolType.PATIENT_PROTOCOL_DISCONTINUED:
                          visitSortIndex = -2;
                          break;
                        case PatientProtocolType.PATIENT_PROTOCOL_SCREEN_FAIL:
                          visitSortIndex = -3;
                          break;
                        default:
                          visitSortIndex =
                            protocol.__typename === 'PatientVisit' ? protocol.order_by : -1;
                      }

                      return {
                        id: protocol.id,
                        name: protocol.name,
                        visitSortIndex,
                      };
                    })
                    .sort(({ visitSortIndex }, { visitSortIndex: visitSortIndex2 }) =>
                      Utils.alphaNumSort(visitSortIndex, visitSortIndex2)
                    );

                  this.gridAPI?.setGridOption('columnDefs', [
                    ...(this.gridAPI?.getColumnDefs() || []).filter(
                      (col) => col.headerName !== 'Visits'
                    ),
                    getPatientVisitColumns({
                      currentOpenMonth: this.mainQuery.getValue().currentOpenMonth,
                      getFormattedTooltip: PatientTrackerComponent.getFormattedTooltip,
                      protocols: columns ?? [],
                      selectedVisibleCurrency$: this.selectedVisibleCurrency$,
                      showPannedEOT: this.showPannedEOT as Signal<boolean>,
                      showPlannedVisits: this.showPlannedVisits,
                    }),
                  ]);

                  this.setVisibilityVisitColumns();
                })
              )
            : of();
        })
      )
      .subscribe();
  }

  generatePinnedBottomData(rows: PatientTrackerRow[]) {
    const data = this.getTotalRowData(rows);

    this.gridAPI?.setGridOption('pinnedBottomRowData', [
      {
        ...data,
        contractCurrency: rows[0]?.contractCurrency || Currency.USD,
        currency: Currency.USD,
        external_patient_id: 'Total',
      },
    ]);
  }

  static getFormattedTooltip = ({ valueFormatted }: ITooltipParams): null | string | undefined =>
    valueFormatted !== Utils.zeroHyphen ? valueFormatted : '';

  private getGroupId(patientGroupId: null | string): null | string {
    return patientGroupId !== this.visitCostsOptionValue ? (patientGroupId as string) : null;
  }

  private getTotalRowData(rows: PatientTrackerRow[]) {
    return rows.reduce(
      (acc, val) => {
        Object.entries(val).forEach(([column, value]) => {
          const shouldCalculate = this.shouldDisplayCell(column, val);

          if (typeof value === 'number' && shouldCalculate) {
            acc[column] ||= 0;
            acc[column] += value || 0;
          }
        });

        return acc;
      },
      { actual_cost: 0, forecast_cost: 0, other: 0, visit: 0 } as Record<string, number>
    );
  }

  private getVisibleRowData() {
    const rows = this.gridAPI
      ?.getRenderedNodes()
      ?.map<PatientTrackerRow>((row) => row.data)
      ?.filter(Boolean);

    return rows ?? [];
  }

  private gridRowsHaveOneCurrency(gridRows: PatientTrackerRow[]) {
    const currency = gridRows[0]?.contractCurrency ?? Currency.USD;

    return gridRows.every(({ contractCurrency }) => contractCurrency === currency);
  }

  private initializeDataStream = (): void => {
    this.datasource.initialize({
      filters: this.serverSideFilters,
      filterValues$: this.filterValues$,
      parseFunction: (items) => {
        setTimeout(() => {
          this.renderPinnedRow(this.selectedVisibleCurrency$.getValue());
        });

        const gridRows = this.patientTrackerScheduleService.parsePatientVisitSchedules(
          this.protocolVersionControl.value ?? '',
          this.mainQuery.getValue().currentOpenMonth,
          items
        );

        this.hasOnlyOneCurrency = this.gridRowsHaveOneCurrency(gridRows);

        return gridRows;
      },
      sortModel$: this.sortModel$,
      untilDestroyedPipeOperator: takeUntilDestroyed(this.destroyRef),
    });
  };

  isCompleted(column: string, val: PatientTrackerRow): boolean {
    return this.isVisitCost(column)
      ? val[`${column.replace(this.visitCostPrefix, '')}::completed`]
      : false;
  }

  get isContractedCurrencySelected() {
    return this.selectedVisibleCurrency$.getValue() === CurrencyToggle.CONTRACTED;
  }

  get visitCostPrefix(): string {
    return this.isContractedCurrencySelected
      ? `${this.columnSuffix}::contracted`
      : this.columnSuffix;
  }

  isVisitCost(column: string): boolean {
    return column.endsWith(this.visitCostPrefix);
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.initializeDataStream();

    this.gridAPI?.setGridOption('columnDefs', [
      getPatientsColumn(
        (event) => {
          const { site_id } = event.data;

          if (!site_id) return;

          void this.router.navigate([
            `${ROUTING_PATH.INVESTIGATOR.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.INDEX}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.INDEX}/${site_id}/${ROUTING_PATH.INVESTIGATOR.SITE_COSTS.SITES.DETAILS}`,
          ]);
        },
        (event) => this.openInvestigatorTransAction(event)
      ),
      getPatientCostColumns(
        PatientTrackerComponent.getFormattedTooltip,
        this.selectedVisibleCurrency$,
        this.showForecastCostThroughEOT.value
      ),
    ]);

    // Default sorting
    this.gridAPI.applyColumnState({
      state: [{ colId: 'site_no', sort: 'asc', sortIndex: 1 }],
    });
  }

  onPaginationChange() {
    this.renderPinnedRow(this.selectedVisibleCurrency$.getValue());
  }

  openInvestigatorTransAction(event: CellClickedEvent) {
    void this.router.navigate([this.investigatorTransactionRoute], {
      queryParams: { external_patient_id: event.value },
    });
  }

  private renderPinnedRow(selectedCurrency: CurrencyToggle) {
    if (selectedCurrency === this.currencyToggle.PRIMARY || this.hasOnlyOneCurrency) {
      this.generatePinnedBottomData(this.getVisibleRowData());
    } else {
      this.clearPinnedBottomRow();
    }
  }

  private setVisibilityVisitColumns() {
    const columnDefs: ColDef[] | undefined = this.gridAPI
      ?.getColumnDefs()
      ?.map((columns) => ('children' in columns ? columns.children : []))
      .flat();

    const colIds: string[] = [];

    columnDefs?.forEach((x) => {
      if ('field' in x && x.field?.includes('::')) {
        colIds.push(x.field);
      }
    });

    const dateColIds = colIds.filter((x) => x.includes('::dates'));
    const costsColIds = colIds.filter((x) => x.includes('::costs'));

    const value = this.display$.getValue();
    const toggleVisibleColumns = this.updateColumnsVisibility(value, dateColIds, costsColIds);

    if (this.gridAPI) {
      toggleVisibleColumns(this.gridAPI);
    }

    if (this.exportGridApi) {
      toggleVisibleColumns(this.exportGridApi);
    }

    this.gridAPI?.sizeColumnsToFit();
  }

  private isPlannedVisit(column: string, val: PatientTrackerRow, suffix: string): boolean {
    return (
      this.showPlannedVisits() &&
      !this.isCompleted(column, val) &&
      val[`${column.replace(suffix, '')}::shouldHighlight`]
    );
  }

  private isPlannedThroughEOT(column: string, val: PatientTrackerRow, suffix: string): boolean {
    return this.showPannedEOT() && val[`${column.replace(suffix, '')}::isPlannedThroughEOT`];
  }

  shouldDisplayCell(column: string, val: PatientTrackerRow) {
    return (
      this.shouldIncludeHiddenCellsFromCalculation(column, val) ||
      this.isPlannedVisit(column, val, this.visitCostPrefix) ||
      this.isPlannedThroughEOT(column, val, this.visitCostPrefix)
    );
  }

  shouldIncludeHiddenCellsFromCalculation(column: string, val: PatientTrackerRow) {
    return !this.isVisitCost(column) || this.isCompleted(column, val);
  }

  onExport = async () => {
    this.exportLoading.set(true);

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: this.mainQuery.getSelectedTrial()?.id || '',
        entity_type: EntityType.TRIAL,
        payload: JSON.stringify({
          export_type: ExportType.PATIENT_TRACKER,
          filename: 'auxilius-patient-tracker',
          filter_model: this.datasource.currentServerInput?.filter_model || [],
          has_only_one_currency: this.hasOnlyOneCurrency,
          is_contracted_currency_selected: this.isContractedCurrencySelected,
          is_hide_patient_costs_selected:
            localStorage.getItem('patient_tracker_page_patient_costs') === 'false',
          month: this.mainQuery.getValue().currentOpenMonth,
          planned_through_EOT: this.showPannedEOT(),
          planned_through_open_month: this.showPlannedVisits(),
          planned_through_open_month_value: this.plannedThroughCurrentMonth(),
          protocol_version_id: this.protocolVersionControl.value,
          sort_model: this.sortModel$.getValue() || [],
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }

    this.exportLoading.set(false);
  };

  private updateColumnsVisibility =
    (value: 'costs' | 'dates', dateColIds: string[], costsColIds: string[]) =>
    (gridApi: GridApi) => {
      AgSetColumnsVisible({
        gridApi: gridApi,
        keys: dateColIds,
        visible: value === 'dates',
      });
      AgSetColumnsVisible({
        gridApi: gridApi,
        keys: costsColIds,
        visible: value === 'costs',
      });
    };
}
